import { motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "@radix-ui/themes";
import {
  BuildingIcon,
  GridIcon,
  KeyIcon,
  TruckIcon,
  Vehicle2Icon,
  Vehicle3Icon,
  VehicleKeyIcon,
} from "@/shared/icons";
import Button from "@/shared/ui/button/Button";
import { VehicleSpawnCard } from "@/domains/vehicleSpawn/components/VehicleSpawnCard/VehicleSpawnCard";
import { VehicleSpawnStats } from "@/domains/vehicleSpawn/components/VehicleSpawnStats/VehicleSpawnStats";
import { VehicleSpawnTabs } from "@/domains/vehicleSpawn/components/VehicleSpawnTabs/VehicleSpawnTabs";
import { VehicleAccessType, VehicleSpawnInfo } from "@/domains/vehicleSpawn/types";

const tabs = [
  {
    id: VehicleAccessType.All,
    icon: GridIcon,
  },
  {
    id: VehicleAccessType.Personal,
    icon: Vehicle2Icon,
  },
  {
    id: VehicleAccessType.Rental,
    icon: VehicleKeyIcon,
  },
  {
    id: VehicleAccessType.Shared,
    icon: KeyIcon,
  },
  {
    id: VehicleAccessType.Family,
    icon: BuildingIcon,
  },
  {
    id: VehicleAccessType.Commercial,
    icon: TruckIcon,
  },
];

export type VehicleSpawnMenuProps = {
  vehicles: VehicleSpawnInfo[];
  onSpawnClick: (vehicleId: number) => void;
  isLoading: boolean;
};

export function VehicleSpawnMenu({ vehicles, onSpawnClick, isLoading }: VehicleSpawnMenuProps) {
  const { t } = useTranslation("vehicle");
  const [currentTab, setCurrentTab] = useState<string>(VehicleAccessType.All);
  const tabsWithCount = tabs.map((tab) => ({
    ...tab,
    count:
      tab.id === VehicleAccessType.All
        ? vehicles.length
        : vehicles.filter((vehicle) => vehicle.accessType === tab.id).length,
  }));
  const filteredVehicles = vehicles.filter(
    (vehicle) => currentTab === VehicleAccessType.All || vehicle.accessType === currentTab,
  );
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const showStats = false;

  return (
    <div className="bg-dark-900 h-[785px] rounded-2xl p-[2rem] grid grid-cols-[200px_1fr] gap-6">
      <div className="flex flex-col gap-6">
        <VehicleSpawnTabs
          tabs={tabsWithCount}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          isLoading={isLoading}
        />
        {showStats && <VehicleSpawnStats total={10} home={4} rental={2} additional={0} />}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center w-[685px] h-[600px]">
          <Spinner className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!isLoading && (
        <div>
          <div className="flex flex-col gap-4">
            <h2 className="text-18 font-bold mb-1 mt-1">{t("spawn.chooseVehicle")}</h2>
            <div className="grid grid-cols-3 grid-rows-[repeat(auto-fill,220px)] gap-[6px] w-[685px] h-[600px] overflow-y-auto overflow-x-hidden pr-[10px]">
              {filteredVehicles.map((vehicle, index) => (
                <motion.div
                  key={`${currentTab}-${vehicle.vehicleId}`}
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    duration: 0.15,
                    delay: index * 0.02,
                    ease: "easeOut",
                  }}
                >
                  <VehicleSpawnCard
                    {...vehicle}
                    isActive={selectedId === vehicle.vehicleId}
                    onClick={() => setSelectedId(vehicle.vehicleId)}
                  />
                </motion.div>
              ))}

              {filteredVehicles.length === 0 && (
                <div className="flex flex-col items-center justify-center w-full h-full col-span-3 row-span-3 gap-3">
                  <Vehicle3Icon className="w-10 h-10 text-neutral-600" />
                  <p className="text-14 font-bold text-neutral-600 uppercase whitespace-pre-line leading-tight">
                    {t("spawn.noVehicles")}
                  </p>
                </div>
              )}
            </div>
          </div>

          <Button
            variant="solid"
            color="white"
            className="w-full mt-4 disabled:opacity-30 disabled:pointer-events-none"
            disabled={!selectedId}
            onClick={() => onSpawnClick(selectedId)}
          >
            {t("spawn.spawn")}
          </Button>
        </div>
      )}
    </div>
  );
}
